import { useState, useEffect, KeyboardEvent } from "react";
import {
  Dialog,
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
} from "@mui/material";
import LockRoundedIcon from "@mui/icons-material/LockRounded";

const DEV_PASSWORD = "hims1111";

interface DevModeModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function DevModeModal({
  open,
  onClose,
  onSuccess,
}: DevModeModalProps) {
  const [pw, setPw] = useState("");
  const [error, setError] = useState(false);

  // 열릴 때마다 초기화
  useEffect(() => {
    if (open) {
      setPw("");
      setError(false);
    }
  }, [open]);

  const handleConfirm = () => {
    if (pw === DEV_PASSWORD) {
      onSuccess();
      onClose();
    } else {
      setError(true);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") handleConfirm();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0,0,0,0.25)",
            backdropFilter: "blur(4px)",
          },
        },
      }}
      PaperProps={{
        sx: {
          width: 380,
          borderRadius: "14px",
          overflow: "hidden",
          backgroundColor: "rgba(250,250,252,0.92)",
          backdropFilter: "blur(20px)",
          border: "1px solid var(--dk-border, rgba(0,0,0,0.08))",
          boxShadow: "0 20px 60px rgba(0,0,0,0.28)",
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
        },
      }}
    >
      {/* 신호등 헤더 */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          px: 2,
          py: 1.25,
          borderBottom: "1px solid var(--dk-border, rgba(0,0,0,0.06))",
        }}
      >
        {["#ff5f57", "#febc2e", "#28c840"].map((c) => (
          <Box
            key={c}
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: c,
            }}
          />
        ))}
      </Box>

      <Box sx={{ px: 3, py: 3, textAlign: "center" }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            mx: "auto",
            mb: 1.5,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0,122,255,0.12)",
          }}
        >
          <LockRoundedIcon sx={{ color: "#007AFF", fontSize: 26 }} />
        </Box>

        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 600,
            color: "var(--dk-text, #1d1d1f)",
            letterSpacing: "-0.01em",
          }}
        >
          개발자 모드 활성화
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: "var(--dk-text-2, #86868b)", mt: 0.5, mb: 2.5 }}>
          계속하려면 비밀번호를 입력하세요
        </Typography>

        <TextField
          autoFocus
          fullWidth
          type="password"
          value={pw}
          onChange={(e) => {
            setPw(e.target.value);
            setError(false);
          }}
          onKeyDown={handleKeyDown}
          error={error}
          helperText={error ? "비밀번호가 올바르지 않습니다" : " "}
          placeholder="비밀번호"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockRoundedIcon sx={{ fontSize: 16, color: "var(--dk-text-2, #86868b)" }} />
              </InputAdornment>
            ),
            sx: {
              borderRadius: "9px",
              backgroundColor: "var(--dk-surface, #fff)",
              fontSize: 14,
              "& fieldset": { borderColor: "var(--dk-border, rgba(0,0,0,0.12))" },
            },
          }}
          FormHelperTextProps={{
            sx: { fontSize: 11.5, mx: 0.5, minHeight: 16 },
          }}
        />

        {/* 액션 버튼 */}
        <Box sx={{ display: "flex", gap: 1.25, mt: 1 }}>
          <Button
            fullWidth
            onClick={onClose}
            disableElevation
            sx={macBtn("secondary")}
          >
            취소
          </Button>
          <Button
            fullWidth
            onClick={handleConfirm}
            disableElevation
            sx={macBtn("primary")}
          >
            확인
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

/** Mac 스타일 버튼 sx */
export function macBtn(kind: "primary" | "secondary" | "danger") {
  const base = {
    textTransform: "none" as const,
    fontSize: 13.5,
    fontWeight: 500,
    py: 1,
    borderRadius: "9px",
    letterSpacing: "-0.01em",
    transition: "all 0.15s ease",
    "&:active": { transform: "scale(0.97)" },
  };
  if (kind === "primary") {
    return {
      ...base,
      color: "#fff",
      backgroundColor: "#007AFF",
      "&:hover": { backgroundColor: "#0067d6" },
    };
  }
  if (kind === "danger") {
    return {
      ...base,
      color: "#ff3b30",
      backgroundColor: "rgba(255,59,48,0.08)",
      "&:hover": { backgroundColor: "rgba(255,59,48,0.14)" },
    };
  }
  return {
    ...base,
    color: "var(--dk-text, #1d1d1f)",
    backgroundColor: "rgba(0,0,0,0.05)",
    "&:hover": { backgroundColor: "rgba(0,0,0,0.09)" },
  };
}
